// =============================================================================
// app/src/lib/orcamentoPdf.ts — Serginho Autopeças
// O orçamento em PDF, com a marca, o CNPJ e o endereço da loja.
//
// Pedido do dono (17/08/2026): "imprimir um orçamento para o cliente com a
// logo da loja, CNPJ, valores, entre outras informações necessárias".
//
// DE ONDE VÊM OS DADOS DA LOJA: de `site/src/config/negocio.ts`, o mesmo
// arquivo que alimenta o site — importado pelo alias `@negocio`, e não
// copiado. A regra 5 do CLAUDE.md diz que dado de negócio mora ali e que
// campo não confirmado é `null`, nunca um placeholder plausível; duas cópias
// seriam duas verdades, e um dia só uma seria corrigida.
//
// Campo nulo simplesmente NÃO SAI no papel. Um orçamento com "CNPJ: —" é pior
// que um sem a linha do CNPJ.
//
// A MARCA sai desenhada de verdade (o símbolo S), pelos mesmos caminhos que a
// tela usa (lib/marca.ts) — não há uma segunda cópia do desenho.
// =============================================================================

import { negocio } from '@negocio';
import { CAMINHOS_MARCA, VIEWBOX_MARCA } from './marca';
import { interpretarCaminho } from './svgPath';
import {
  baixarPdf, novoPdf, quebrarEmLinhas, type Pdf,
} from './pdf';

const MARGEM = 18;
const LARGURA = 210;
const DIREITA = LARGURA - MARGEM;

export interface ItemDoOrcamento {
  /** Congelado no banco; NÃO sai no papel desde 18/08/2026. */
  sku?: string;
  descricao: string;
  quantidade: number;
  preco_unit: number;
  subtotal: number;
}

export interface DadosOrcamento {
  codigo: string;
  emitido_em: string;
  valido_ate: string | null;
  cliente_nome: string | null;
  cliente_contato: string | null;
  /** 049 — o carro que o cliente citou. Sai no papel: é o que ele confere. */
  veiculo?: string | null;
  vendedor: string | null;
  observacao: string | null;
  desconto: number;
  total: number;
  itens: ItemDoOrcamento[];
}

/**
 * O orçamento do banco vira os dados do PDF — o documento que o cliente leva
 * embora ou recebe pelo WhatsApp.
 *
 * POR QUE É UMA FUNÇÃO, e não um objeto montado na tela (10/09/2026): porque a
 * tela do orçamento agora MOSTRA o código da peça para a equipe, e o código não
 * pode atravessar para cá. Montar o objeto à mão no meio do JSX é exatamente o
 * arranjo que deixou a taxa de entrega fora do recibo por semanas — campo
 * esquecido (ou campo a mais) em objeto literal é erro que o TypeScript não
 * pega quando o tipo tem opcionais. Aqui a passagem é explícita, curta e
 * testada: o que não está escrito nesta função não chega ao papel.
 */
export function orcamentoParaPdf(o: {
  codigo: string;
  created_at: string;
  valido_ate: string | null;
  cliente_nome: string | null;
  cliente_contato: string | null;
  veiculo?: string | null;
  vendedor: string | null;
  observacao: string | null;
  desconto: number | string | null;
  total: number | string;
  itens: Array<{
    descricao: string;
    quantidade: number | string;
    preco_unit: number | string;
    subtotal: number | string;
  }>;
}): DadosOrcamento {
  return {
    codigo: o.codigo,
    emitido_em: o.created_at,
    valido_ate: o.valido_ate,
    cliente_nome: o.cliente_nome,
    cliente_contato: o.cliente_contato,
    veiculo: o.veiculo ?? null,
    vendedor: o.vendedor,
    observacao: o.observacao,
    desconto: Number(o.desconto ?? 0),
    total: Number(o.total),
    // SÓ ESTES QUATRO CAMPOS. Nada de sku, codigo_avulso ou código de
    // fabricante: o papel do cliente leva nome, quantidade e valor.
    itens: o.itens.map((i) => ({
      descricao: i.descricao,
      quantidade: Number(i.quantidade),
      preco_unit: Number(i.preco_unit),
      subtotal: Number(i.subtotal),
    })),
  };
}

const brl = (n: number) => `R$ ${n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
const dataBR = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');

/** O endereço numa linha só, pulando o que não está confirmado. */
export function enderecoDaLoja(): string {
  const e = negocio.endereco;
  const rua = [e.rua, e.numero].filter(Boolean).join(', ');
  // cidade/uf ficam na RAIZ de negocio, não dentro de endereco.
  return [rua, e.bairro, [negocio.cidade, negocio.uf].filter(Boolean).join('/'), e.cep]
    .filter((p) => p && String(p).trim())
    .join(' · ');
}

/** Telefone em formato legível: +555332311580 → (53) 3231-1580. */
export function telefoneLegivel(bruto: string | null): string | null {
  if (!bruto) return null;
  const d = bruto.replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return bruto;
}

function cabecalho(pdf: Pdf): number {
  // O símbolo, à esquerda; a identificação, ao lado.
  const LADO = 16;
  for (const d of CAMINHOS_MARCA) {
    pdf.caminho(interpretarCaminho(d), VIEWBOX_MARCA as unknown as
      readonly [number, number, number, number],
      { xMm: MARGEM, yMm: MARGEM, larguraMm: LADO }, 0.1);
  }

  const x = MARGEM + LADO + 6;
  pdf.texto(negocio.nome, x, MARGEM + 5, { tamanho: 16, negrito: true });

  let y = MARGEM + 10;
  const linha = (t: string | null) => {
    if (!t) return;
    pdf.texto(t, x, y, { tamanho: 8, cinza: 0.35 });
    y += 3.6;
  };
  if (negocio.cnpj) linha(`CNPJ ${negocio.cnpj}`);
  linha(enderecoDaLoja());
  const fones = [telefoneLegivel(negocio.telefoneFixo), telefoneLegivel(negocio.whatsapp)]
    .filter(Boolean).join(' · ');
  linha(fones || null);

  const base = Math.max(y, MARGEM + LADO) + 4;
  pdf.linha(MARGEM, base, DIREITA, base, { espessura: 0.6 });
  return base + 8;
}

export function montarPdfOrcamento(o: DadosOrcamento): Uint8Array {
  const pdf = novoPdf();
  let y = cabecalho(pdf);

  pdf.texto('ORÇAMENTO', MARGEM, y, { tamanho: 13, negrito: true });
  pdf.texto(o.codigo, DIREITA, y, { tamanho: 13, negrito: true, alinhamento: 'direita' });
  y += 7;

  pdf.texto(`Emitido em ${dataBR(o.emitido_em)}`, MARGEM, y, { tamanho: 9, cinza: 0.35 });
  if (o.valido_ate) {
    pdf.texto(`Válido até ${dataBR(o.valido_ate)}`, DIREITA, y,
      { tamanho: 9, negrito: true, alinhamento: 'direita' });
  }
  y += 5;
  if (o.cliente_nome) {
    pdf.texto(`Cliente: ${o.cliente_nome}${o.cliente_contato ? ` · ${o.cliente_contato}` : ''}`,
      MARGEM, y, { tamanho: 9 });
    y += 5;
  }
  if (o.veiculo) {
    pdf.texto(`Veículo: ${o.veiculo}`, MARGEM, y, { tamanho: 9 });
    y += 5;
  }
  if (o.vendedor) {
    pdf.texto(`Atendimento: ${o.vendedor}`, MARGEM, y, { tamanho: 9, cinza: 0.35 });
    y += 5;
  }
  y += 3;

  // --- Itens ---------------------------------------------------------------
  const COL_QTD = 118;
  const COL_UNIT = 150;
  const COL_SUB = DIREITA;

  pdf.retangulo(MARGEM, y - 4, DIREITA - MARGEM, 6, 0.92);
  pdf.texto('PEÇA', MARGEM + 2, y, { tamanho: 8, negrito: true });
  pdf.texto('QTD', COL_QTD, y, { tamanho: 8, negrito: true, alinhamento: 'direita' });
  pdf.texto('UNIT.', COL_UNIT, y, { tamanho: 8, negrito: true, alinhamento: 'direita' });
  pdf.texto('TOTAL', COL_SUB, y, { tamanho: 8, negrito: true, alinhamento: 'direita' });
  y += 7;

  for (const item of o.itens) {
    // A descrição pode ser longa (peça diversa descrita à mão); quebra em
    // linhas em vez de vazar por cima da coluna de quantidade.
    const linhas = quebrarEmLinhas(item.descricao, COL_QTD - MARGEM - 8, 9);
    linhas.forEach((linha, i) => {
      pdf.texto(linha, MARGEM + 2, y + i * 4, { tamanho: 9 });
    });
    pdf.texto(String(item.quantidade).replace('.', ','), COL_QTD, y,
      { tamanho: 9, alinhamento: 'direita' });
    pdf.texto(brl(item.preco_unit), COL_UNIT, y, { tamanho: 9, alinhamento: 'direita' });
    pdf.texto(brl(item.subtotal), COL_SUB, y, { tamanho: 9, alinhamento: 'direita' });
    y += Math.max(linhas.length, 1) * 4 + 1.5;

    // O SKU saiu do papel em 18/08/2026 (pedido do dono: "o número do código
    // da peça no sistema não precisava aparecer"). Ele continua congelado em
    // quote_items — é rastreio interno, não informação de cliente.
    pdf.linha(MARGEM, y - 1.5, DIREITA, y - 1.5, { cinza: 0.85 });
    y += 1.5;
  }

  // --- Totais ---------------------------------------------------------------
  y += 4;
  const bruto = o.itens.reduce((s, i) => s + i.subtotal, 0);
  if (o.desconto > 0) {
    pdf.texto('Subtotal', COL_UNIT, y, { tamanho: 9, alinhamento: 'direita' });
    pdf.texto(brl(bruto), COL_SUB, y, { tamanho: 9, alinhamento: 'direita' });
    y += 5;
    pdf.texto('Desconto', COL_UNIT, y, { tamanho: 9, alinhamento: 'direita' });
    pdf.texto(`- ${brl(o.desconto)}`, COL_SUB, y, { tamanho: 9, alinhamento: 'direita' });
    y += 5;
  }
  pdf.texto('TOTAL', COL_UNIT, y, { tamanho: 12, negrito: true, alinhamento: 'direita' });
  pdf.texto(brl(o.total), COL_SUB, y, { tamanho: 12, negrito: true, alinhamento: 'direita' });
  y += 10;

  if (o.observacao) {
    for (const linha of quebrarEmLinhas(o.observacao, DIREITA - MARGEM, 8)) {
      pdf.texto(linha, MARGEM, y, { tamanho: 8, cinza: 0.35 });
      y += 3.6;
    }
    y += 3;
  }

  // O recado que evita discussão no balcão duas semanas depois.
  pdf.linha(MARGEM, y, DIREITA, y, { cinza: 0.85 });
  y += 5;
  const aviso = o.valido_ate
    ? `Preços válidos até ${dataBR(o.valido_ate)}, sujeitos à disponibilidade em estoque. `
      + 'Este documento é um orçamento e não vale como comprovante de compra.'
    : 'Preços sujeitos a alteração e à disponibilidade em estoque. Este documento é '
      + 'um orçamento e não vale como comprovante de compra.';
  for (const linha of quebrarEmLinhas(aviso, DIREITA - MARGEM, 7.5)) {
    pdf.texto(linha, MARGEM, y, { tamanho: 7.5, cinza: 0.45 });
    y += 3.4;
  }

  return pdf.bytes();
}

export function nomeDoArquivoOrcamento(o: DadosOrcamento): string {
  return `orcamento-${o.codigo.replace(/[^\w-]/g, '')}.pdf`;
}

export function baixarOrcamento(o: DadosOrcamento): void {
  baixarPdf(montarPdfOrcamento(o), nomeDoArquivoOrcamento(o));
}
